import { useMutation, useQuery } from '@apollo/client';
import { Empty, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ApplicantProfileContactContainerApplicantUserDocument,
  ApplicantProfileContactContainerApplicantUserUpdateDocument,
  ApplicantUserContactInformationInput
} from '../../../../generated';
import { lookupCountryQuery } from './profile-contact-country-query';
import { Country } from '../../../../types/country';
import { ComponentQueryLoader } from '../../../shared/component-query-loader';
import { getItem } from '../../../shared/local-storage';
import { ProfileContactEdit } from './profile-contact-edit';

export const ProfileContactEditContainer = () => {
  const navigate = useNavigate();
  const applicantUserId = getItem('applicantUserId');

  const {
    data: contactData,
    loading: contactLoading,
    error: contactError
  } = useQuery(ApplicantProfileContactContainerApplicantUserDocument, {
    variables: {
      applicantUserId
    }
  });

  const { data: countryData, error: countryError, loading: countryLoading } = useQuery(lookupCountryQuery);

  const [applicantUserUpdate] = useMutation(ApplicantProfileContactContainerApplicantUserUpdateDocument);

  const handleSave = async (data: ApplicantUserContactInformationInput) => {
    try {
      const result = await applicantUserUpdate({
        variables: {
          input: {
            id: applicantUserId,
            applicantUserDetails: {
              personalInformation: {
                contactInformation: {
                  address: data?.address,
                  phone: data?.phone,
                  emailAddress: data?.emailAddress
                }
              }
            }
          }
        }
      });
      if (result.data?.applicantUserUpdate.status.success) {
        message.success('Contact details updated');
        handleEdit();
      } else {
        message.error('Failed to update Contact details');
      }
    } catch (error) {
      message.error(`Error Updating Contact: ${JSON.stringify(error)}`);
    }
  };

  const isCountryArray = (data: unknown): data is Country[] => Array.isArray(data) && data.every((item) => 'countryName' in item && 'countryCode' in item);

  const countryDataFiltered = isCountryArray(countryData?.countries?.eventPayload?.countries) ? countryData.countries.eventPayload.countries : [];

  const handleEdit = () => {
    const currentPath = location.pathname;
    const newPath = currentPath.replace(/\/[^/]+$/, '/view');
    navigate(newPath);
  };

  if (contactError || !contactData?.applicantUser?.personalInformation?.contactInformation) {
    return <Empty description="There was an error loading the data. Please try again!" />;
  }

  if (countryError || !countryData?.countries?.eventPayload?.countries) {
    return <Empty description={`There was an error loading the country data. Please try again! ${countryError}`} />;
  }

  return (
    <ComponentQueryLoader
      error={contactError || countryError}
      loading={contactLoading || countryLoading}
      hasData={true}
      hasDataComponent={
        <ProfileContactEdit
          contactInformation={{
            ...contactData?.applicantUser?.personalInformation?.contactInformation,
            phone: contactData?.applicantUser?.personalInformation?.contactInformation?.phone?.map((phone) => {
              return {
                id: phone?.id,
                phoneNumber: phone?.phoneNumber,
                phoneType: phone?.phoneType,
                isPrimary: phone?.phoneNumber === contactData?.applicantUser?.personalInformation?.contactInformation?.primaryPhone
              };
            })
          }}
          countryData={countryDataFiltered}
          onCancel={handleEdit}
          onSave={handleSave}
        />
      }
    />
  );
};

